<template>
  <div>
    <Section>
      <SectionHeader
        :title="$t('apps.payroll.message.date')"
        :subtitle="`${$t('series.prl')} ${prlCode}`"
      />
      <FormRow>
        <Col className="col-lg-2">
        <Label :text="`${$t('apps.payroll.label.datestart')} *`" />
        <BaseInputDatePicker
          v-model="$v.input.dateStart.$model"
          :classNames="[{ 'is-invalid': $v.input.dateStart.$error }]"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-2">
        <Label :text="`${$t('apps.payroll.label.dateend')} *`" />
        <BaseInputDatePicker
          v-model="$v.input.dateEnd.$model"
          :classNames="[{ 'is-invalid': $v.input.dateEnd.$error }]"
          :disabled="disabled"
        />
        </Col>
        <Col
          v-if="input.dateEnd && input.dateEnd"
          className="col-lg-1">
        <Label :text="'&nbsp;'" />
        <BaseInput
          :value="$t('apps.payroll.message.daterange', { number: diffDays })"
          :disabled="true"
        />
        </Col>
      </FormRow>
    </Section>

    <Section v-if="isCreatePage">
      <SectionHeader :title="$t('apps.payroll.message.group')"/>
      <FormRow>
        <Col
          v-if="!groups.length"
          className="col-lg-12"
        >
        <Label :text="$t('apps.payroll.label.employeegroup')" />
        <BaseNotFoundData
          :title="$t('apps.payroll.message.notfoundgroups')"
          :detail="$t('apps.payroll.message.createnewgroup')"
        />
        </Col>
        <Col
          v-else
          className="col-lg-4"
        >
        <Label :text="$t('apps.payroll.label.employeegroup')" />
        <BaseDropdown
          v-model="input.groupId"
          :options="groupOptions"
          :disabled="disabled"
          @change="handleChangeGroup"
        />
        </Col>
      </FormRow>
    </Section>

    <Section v-if="isCreatePage">
      <SectionHeader :title="$t('apps.payroll.message.condition')"/>
      <FormRow>
        <Col
          v-if="!conditions.length"
          className="col-lg-12"
        >
        <BaseNotFoundData
          :title="$t('apps.payroll.message.notfoundconditions')"
          :detail="$t('apps.payroll.message.createnewcondition')"
        />
        </Col>
        <Col
          v-else
          className="col-lg-12"
        >
        <div class="row">
          <div
            v-for="(item, index) in conditions"
            v-if="!item.deletedAt"
            :key="index"
            class="col-2 mb-3"
          >
            <BaseCheckbox
              v-model="input.conditionIds"
              :label="item.name"
              :val="item.conditionId"
              :disabled="disabled"
            />
          </div>
        </div>
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.payroll.message.employeelist')"/>
      <FormRow>
        <Col
          v-if="!filterEmployees.length"
          className="col-lg-12"
        >
        <BaseNotFoundData
          :title="$t('apps.payroll.message.notfoundemployeee')"
          :detail="$t('apps.payroll.message.createnewemployee')"
        />
        </Col>
        <Col
          v-else
          className="col-lg-12"
        >
        <BasePaySlipEmployee
          v-if="isRender"
          v-model="input.employees"
          :conditionIds="input.conditionIds"
          :conditions="conditions"
          :employees="filterEmployees"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <FormRow>
        <Col className="col-lg-6">
        <BaseTextArea
          v-model="input.note"
          :placeholder="$t('placeholder.note')"
          :disabled="disabled"
          :rows="3"
        />
        </Col>
        <Col className="col-lg-4 ml-auto">
        <div class="row">
          <div class="col-6">
            {{ $t('apps.payroll.message.employeelistcount') }}
          </div>
          <div class="col-6 text-right">
            {{ selectedEmployeeCount }}
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-6">
            {{ $t('apps.payroll.message.grandtotal') }}
          </div>
          <div class="col-6 text-right">
            <span class="text-underline-border">
              {{ getNumber(grandTotal) }}
            </span>
          </div>
        </div>
        </Col>
      </FormRow>
    </Section>

    <BaseFormAction
      v-if="edit && showActionButtons"
      :textCancel="$t('button.cancel')"
      :textSubmit="$t('button.save')"
      :isSubmitted="isSubmitted"
      @onCancel="handleCancel"
      @onSubmit="handleSubmit"
    />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { cloneDeep } from 'lodash'
import { getDiffDate, toNumber } from '@/helpers'
import { DOCUMENT_STATUS } from '@/helpers/enum'
import userPermissionMixin from '@/mixins/userPermission'

export default {
  mixins: [userPermissionMixin],

  props: {
    state: Object,
    isSubmitted: Boolean,
    edit: {
      type: Boolean,
      default: true,
    },
    disabled: Boolean,
    prlCode: String,
    groups: Array,
    conditions: Array,
    employees: Array,
    showActionButtons: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      DOCUMENT_STATUS,
      input: {
        groupId: null,
        dateStart: null,
        dateEnd: null,
        conditionIds: [],
        employees: [],
        note: null,
      },
      inputOriginal: null,
      isRender: true,
      deletedEmployees: [],
    }
  },

  validations: {
    input: {
      dateStart: {
        required,
      },
      dateEnd: {
        required,
      },
    },
  },

  computed: {
    employeeIds () {
      return this.employees.map((emp) => emp.empId)
    },
    filterEmployees () {
      const employees = this.employees
      return cloneDeep([
        ...employees,
        ...this.deletedEmployees,
      ])
    },
    isCreatePage () {
      return !this.state
    },
    isEditPage () {
      return !!this.state
    },
    defaultOption () {
      return {
        text: this.$t('placeholder.select'),
        value: null,
      }
    },
    groupOptions () {
      const items = this.groups.map((item) => (
        {
          text: `${item.name} (${item.appliedCount})`,
          value: item.empGroupId,
          disabled: item.appliedCount === 0,
        }
      ))
      return [ { ...this.defaultOption }, ...items]
    },
    selectedEmployees () {
      return this.input.employees.reduce((acc, cur) => {
        if (cur.empId && !cur.deleted) {
          acc.push(cur)
        }
        return acc
      }, [])
    },
    selectedEmployeeCount () {
      return this.selectedEmployees.length
    },
    grandTotal () {
      return this.selectedEmployees.reduce((total, cur) => total += toNumber(cur.net), 0)
    },
    diffDays () {
      const { dateStart, dateEnd } = this.input
      if (dateStart && dateEnd) {
        return getDiffDate(dateStart, dateEnd, 'days')
      } else {
        return null
      }
    },
  },

  watch: {
    input: {
      handler: function () {
        this.$emit('update:form', this.input)
      },
      deep: true,
    },
  },

  created () {
    if (this.isEditPage) {
      this.setInput(this.state)
      this.setDeletedEmployees()
    } else {
      this.setDefaultCondition()
      this.inputOriginal = cloneDeep(this.input)
    }
  },

  methods: {
    handleCancel () {
      this.input = cloneDeep(this.inputOriginal)
      this.ReRenderEmployeeComponent()
      this.scrollToTop(600)
      this.$emit('onCancel')
    },
    handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid || this.selectedEmployeeCount === 0) {
        this.input.employees
          .filter((emp) => !emp.deleted)
          .forEach((empInput, index) => {
            if (index === 0) {
              empInput.validateForm()
            }
          })
        this.scrollToTop(600)
      } else {
        const input = cloneDeep(this.input)
        input.employees = input.employees.filter((emp) => emp.empId && !emp.deleted)
        this.$emit('onSubmit', input)
      }
    },
    setInput (state) {
      this.input.dateStart = new Date(state.dateStart)
      this.input.dateEnd = new Date(state.dateEnd)
      this.input.conditionIds = state.employees.reduce((acc, cur) => {
        for (const cond of cur.conditions) {
          if (!acc.includes(cond.conditionId)) {
            acc.push(cond.conditionId)
          }
        }
        return acc
      }, [])
      this.input.employees = state.employees.map((emp) => {
        const net = emp.wageAmount + emp.conditions.reduce((total, cur) => total + cur.amount, 0)
        return {
          empId: emp.empId,
          name: emp.name,
          wageAmount: emp.wageAmount,
          net: net,
          conditions: emp.conditions,
          deleted: false,
          conditionsDynamic: false,
          validateForm: null,
        }
      })
      this.input.note = state.note
      this.inputOriginal = cloneDeep(this.input)
    },
    handleChangeGroup (e) {
      const group = this.groups.find((item) => item.empGroupId === e.target.value)
      const emps = group.empIds.reduce((acc, cur) => {
        const emp = this.employees.find((item) => item.empId === cur)
        if (emp) {
          acc.push({
            empId: emp.empId,
            name: emp.name,
            wageAmount: emp.wageAmount === null ? 0 : emp.wageAmount,
            net: 0,
            conditions: [],
            deleted: false,
            conditionsDynamic: true,
            validateForm: null,
          })
        }
        return acc
      }, [])
      this.input.employees = emps
      this.ReRenderEmployeeComponent()
    },
    ReRenderEmployeeComponent () {
      this.isRender = false
      this.$nextTick(() => {
        this.isRender = true
      })
    },
    setDefaultCondition (isCreatePage) {
      this.input.conditionIds = this.conditions
        .filter((item) => item.autoApplyNewEmp)
        .map((item) => item.conditionId)
    },
    setDeletedEmployees () {
      this.deletedEmployees = this.input.employees.reduce((acc, cur) => {
        if (!this.employeeIds.includes(cur.empId) && !acc.includes(cur.empId)) {
          const emp = {
            empId: cur.empId,
            name: cur.name,
            wageAmount: cur.wageAmount,
          }
          acc.push(emp)
        }
        return acc
      }, [])
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/combine/PayrollPaySlipForm.vue